import React, { useState, useEffect } from 'react';

import { useGetAccountInfo } from '@multiversx/sdk-dapp/hooks';
import { BigUIntValue, U32Value } from '@multiversx/sdk-core/out';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Checkbox, checkboxClasses, FormControlLabel } from '@mui/material';
import BigNumber from 'bignumber.js/bignumber';
import clsx from 'clsx';
import { Spinner } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import { useNavigate, createSearchParams } from 'react-router-dom';

import { CustomButton, CustomModal, SwapTabs } from 'components';
import { CustomInput } from 'components/CustomInput';
import { contracts } from 'config/config';

import { BigZero, coinNames, coins } from 'helpers/constant';
import { convertWeiToEsdt, getSymbol, getTokenImg, handleTokenImgError, shortenEsdt, UnwrapToken } from 'helpers/utils';
import { useInteraction } from 'Interaction';
import styles from './styles.module.scss';

import ADD_ICO from 'assets/img/pool/add.png';
import SWAP_ICO from 'assets/img/pool/swap.png';
import REMOVE_ICO from 'assets/img/pool/bag.png';


export const Pools = () => {
  const [pairs, setPairs] = useState<any[]>([]);
  const [currentPair, setCurrentPair] = useState<any>(undefined);
  const [showAddLiquidityModal, setShowAddLiquidityModal] = useState(false);
  const [showRemoveLiquidityModal, setShowRemoveLiquidityModal] = useState(false);
  const [onlyOwnedPool, setOnlyOwnedPool] = useState(false);
  const [onlyYourAssetPool, setOnlyYourAssetPool] = useState(false);
  const [searchKey, setSearchKey] = useState('');

  const [firstTokenAmount, setFirstTokenAmount] = useState<BigNumber>(BigZero);
  const [secondTokenAmount, setSecondTokenAmount] = useState<BigNumber>(BigZero);
  const [lpTokenAmount, setLpTokenAmount] = useState<BigNumber>(BigZero);
  const [expectedWegldAmount,setExpectedWegldAmount] = useState<BigNumber>(BigZero);
  const [egldBalance, setEgldBalance] = useState(BigZero);
  const [egldPair, setEgldPair] = useState(false);

  const [price, setPrice] = useState(BigZero);

  const navigate = useNavigate();
  const { fetching, egldPrice, usdcPrice, pairInfos, callMethod } = useInteraction();
  const { account: {address: userAddress, balance: userBalance} } = useGetAccountInfo();

  const isMobile = useMediaQuery({
    query: '(max-width: 685px)'
  });

  useEffect(() => {
    findValidPools();
  }, [pairInfos, onlyOwnedPool, onlyYourAssetPool]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      findValidPools();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchKey]);

  const findValidPools = () => {
    const pairData = [];

    for(const pair of pairInfos) {
      if(pair.state.name === 'Inactive') continue;

      let valid = 0;

      if(!onlyOwnedPool && !onlyYourAssetPool) {
        valid += 1;
      }

      if(onlyOwnedPool && userAddress && pair?.owner.toString() == userAddress) {
        valid += 1;
      }

      if(onlyYourAssetPool && userAddress && BigZero.lt(pair?.lp_token_balance)) {
        valid += 1;
      }

      if(!valid) continue;

      const first_token_symbol = getSymbol(pair?.first_token_id);
      const second_token_symbol = pair?.second_token_id === coinNames[1] ? 'EGLD' : getSymbol(pair?.second_token_id);

      if(searchKey) {
        if(
          !first_token_symbol.toLowerCase().includes(searchKey.toLowerCase()) &&
          !second_token_symbol.toLowerCase().includes(searchKey.toLowerCase())
        ) continue;
      }
      
      const firstTokenId = pair.first_token_id;
      const secondTokenId = pair.second_token_id;

      let liquidity = BigZero;
      const second_token_reserve = convertWeiToEsdt(pair.second_token_reserve, pair.second_token_decimal.toNumber());

      if(secondTokenId === coinNames[1]) {
        liquidity = second_token_reserve.multipliedBy(2).multipliedBy(egldPrice);
      } else if(secondTokenId === coinNames[2]) {
        liquidity = second_token_reserve.multipliedBy(2).multipliedBy(usdcPrice);
      }

      let pool_share = BigZero;
      if(BigZero.lt(liquidity)) {
        pool_share =  pair.lp_token_balance.dividedBy(convertWeiToEsdt(pair.lp_token_supply, 18));
      }

      pairData.push({
        ...pair,
        liquidity,
        pool_share,
        first_token_symbol,
        second_token_symbol
      });
    }

    setPairs(pairData);
  };

  useEffect(() => {
    const first_token_reserve = currentPair?.first_token_reserve || BigZero;
    const second_token_reserve = currentPair?.second_token_reserve || BigZero;
    
    if(BigZero.lt(first_token_reserve)) {
      setPrice(second_token_reserve.dividedBy(first_token_reserve));
    }
  }, [currentPair]);

  useEffect(() => {
    setEgldBalance(convertWeiToEsdt(userBalance, 18));
  }, [userBalance]);

  useEffect(() => {
    if(!userAddress || !currentPair) return;

    (async () => {
      if(currentPair.second_token_id === coinNames[1]) {
          setEgldPair(true);
      } else {
          setEgldPair(false);
      }
    })();
  }, [userAddress, currentPair]);

  const addLp = async () => {
    if(
      BigZero.gte(firstTokenAmount) || firstTokenAmount.gt(currentPair.first_token_balance) ||
      BigZero.gte(secondTokenAmount) || secondTokenAmount.gt(egldPair ? egldBalance : currentPair.second_token_balance) ||
      !userAddress
    ) return;

    try {
      await callMethod({
          contract: contracts.Dex,
          method: 'addLiquidity',
          args: [
            new BigUIntValue('1'),
            new BigUIntValue('1'),
          ],
          fts: [
              {
                  token: currentPair.first_token_id,
                  amount: firstTokenAmount,
                  decimals: currentPair.first_token_decimal.toNumber()
              },
              {
                  token: currentPair.second_token_id,
                  amount: secondTokenAmount,
                  decimals: currentPair.second_token_decimal.toNumber()
              }
          ],
          wrap: egldPair ? secondTokenAmount : BigZero
      });

      setShowAddLiquidityModal(false);
    } catch(error: any) {
        console.log(error);
    }
  };

  const removeLp = async () => {
    if(
      BigZero.gte(lpTokenAmount) || 
      BigZero.gte(expectedWegldAmount) ||
      !userAddress
    ) return;

    try {
      if(egldPair) {
        await callMethod({
          contract: contracts.Dex,
          method: 'removeLiquidity',
          args: [
            new BigUIntValue('1'),
            new BigUIntValue('1'),
          ],
          fts: [
              {
                  token: currentPair.lp_token_id,
                  amount: lpTokenAmount,
                  decimals: 18
              },
          ]
        });
      } else {
        await callMethod({
          contract: contracts.Dex,
          method: 'removeLiquidity',
          args: [
            new BigUIntValue('1'),
            new BigUIntValue('1'),
          ],
          fts: [
              {
                  token: currentPair.lp_token_id,
                  amount: lpTokenAmount,
                  decimals: 18
              },
          ],
        });
      }

      setShowRemoveLiquidityModal(false);
    } catch(error: any) {
        console.log(error);
    }
  };

  const enableSwap = async (pair: any) => {
    if(pair.state.name !== 'ActiveButNoSwap') return;

    // console.log(pair);

    try {
      await callMethod({
        contract: contracts.Dex,
        method: 'enableSwap',
        args: [
          new U32Value(pair.pair_id)
        ],
        egld: '2'
      });
    } catch(error: any) {
        console.log(error);
    }
  };

  return (
    <>
      <div className='d-flex flex-column flex-fill align-items-center justify-content-start'>
        <div className={clsx(styles.card, 'card shadow-sm mx-5')}>
            <div className={styles.cardBody}>
              <div className={clsx(styles.header, 'd-flex justify-content-between align-items-center w-100')}>
                <div className='d-flex justify-content-between align-items-center'>
                  {
                    isMobile ? (
                      <h4>LIQUIDITY POOLS</h4>
                    ) : (
                      <h2>LIQUIDITY POOLS</h2>
                    )
                  }
                  {/* <Form>
                    <Form.Check
                      className='ml-4'
                      type='switch'
                      id='custom-switch'
                      label='Your Liquidity'
                      value={onlyOwnedPool}
                    />
                  </Form> */}
                </div>
              </div>
              <div className='d-flex w-100 justify-content-end text-white'>
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: 'white',
                        [`&.${checkboxClasses.checked}`]: {
                          color: '#ff9d19',
                        },
                      }}
                      checked={onlyOwnedPool}
                      onChange={(e, checked) => setOnlyOwnedPool(checked)}
                    />
                  }
                  label="Your Pools"
                />

                <FormControlLabel
                  className='ml-2'
                  control={
                    <Checkbox
                      sx={{
                        color: 'white',
                        [`&.${checkboxClasses.checked}`]: {
                          color: '#ff9d19',
                        },
                      }}
                      checked={onlyYourAssetPool}
                      onChange={(e, checked) => setOnlyYourAssetPool(checked)}
                    />
                  }
                  label="Your Assets"
                />
                {/* <span>TVL: $123.25</span>  */}
              </div>
              <div className='w-100 d-flex justify-content-between align-items-center'>
                <input
                    className={clsx(styles.search, 'flex-grow-1 my-4 mr-2')}
                    type='text'
                    placeholder='Search Pool'
                    value={searchKey}
                    onChange={(e) => setSearchKey(e.target.value)}
                />
                <CustomButton
                  className={styles.createButton}
                  onClick={() => {
                    navigate('/pool/create');
                  }}
                >
                  {
                    isMobile ? ' Create' : (
                      <>
                        <i className='fa-solid fa-plus'></i>
                        {' '}Create New Pool
                      </>
                    )
                  }
                </CustomButton>
              </div>
              {
                !isMobile && (
                  <div className={clsx(styles.listHeader, 'd-flex justify-content-between align-items-center row')}>
                    <div className='col-1'>#</div>
                    <div className='col-5'>Pool</div>
                    <div className='col-3 text-right'>Liquidity <i className='fa-solid fa-caret-down' /></div>
                    <div className='col-3 text-center'>LP Reward</div>
                  </div>
                )
              }
              <div className={styles.list}>

                {
                  pairs.length > 0 &&
                    pairs.map((pair, index) => {
                      return (
                        <Accordion
                          key={index}
                          className={clsx(styles.listItem)}
                        >
                          <AccordionSummary
                            sx={{ px: 0 }}
                            expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                          >
                            {
                              !isMobile ? (
                                <div className='col-sm-1'>
                                  {pair?.pair_id.toNumber()}
                                </div>
                              ) : (
                                <></>
                              )
                            }
                            <div className='col-5 col-sm-5 d-flex align-items-center'>
                              <img src={getTokenImg(pair?.first_token_id)} onError={handleTokenImgError}  alt='FIRST' width={24} />
                              <img src={getTokenImg(UnwrapToken(pair?.second_token_id))} onError={handleTokenImgError} alt='' width={24} />
                              {
                                !isMobile && (
                                  <>
                                    <span className='bold ml-2'>{pair?.first_token_symbol} - {pair?.second_token_symbol}</span>
                                  </>
                                )
                              }
                            </div>
                            <div className='col-4 col-sm-3 d-flex align-items-center justify-content-end'>{BigZero.lt(pair?.liquidity) ? `$${shortenEsdt(pair?.liquidity)}` : '-'}</div>
                            <div className='col-3 col-sm-3 d-flex align-items-center justify-content-center'>0.2%</div>
                          </AccordionSummary>
                          <AccordionDetails className='row'>
                            <div className='col-12 col-sm-6 col-md-4 d-flex flex-column'>
                              <span className='text-secondary'>
                                Pool Assets
                              </span>
                              <span>
                                <img src={getTokenImg(pair?.first_token_id)} onError={handleTokenImgError}  alt='FIRST' width={24} /> {shortenEsdt(convertWeiToEsdt(pair?.first_token_reserve, pair?.first_token_decimal.toNumber()))} {pair?.first_token_symbol}
                              </span>
                              <span className='text-secondary'>
                                {pair?.first_token_id}
                              </span>
                              <span>
                              <img src={getTokenImg(UnwrapToken(pair?.second_token_id))} onError={handleTokenImgError} alt='' width={24} /> {shortenEsdt(convertWeiToEsdt(pair?.second_token_reserve, pair?.second_token_decimal.toNumber()))} {pair?.second_token_symbol}
                              </span>
                              <span className='text-secondary'>
                                {pair?.second_token_id}
                              </span>
                            </div>
                            <div className='col-12 col-sm-6 col-md-5 d-flex flex-column mt-4 mt-sm-0'>
                              <span className='text-secondary'>
                                Your Liquidity
                              </span>
                              <span>$ {shortenEsdt(pair?.liquidity.multipliedBy(pair?.pool_share))}</span>
                              <span className='text-secondary'>
                                {shortenEsdt(pair?.lp_token_balance)} {pair?.lp_token_id}
                              </span>
                              <span className='text-secondary'>
                                Pool Share: {shortenEsdt(pair?.pool_share.multipliedBy(100))}%
                              </span>
                            </div>
                            <div className='col-12 col-md-3 d-flex flex-column justify-content-center mt-4 mt-md-0'>
                              <CustomButton
                                className={styles.button}
                                onClick={() => {
                                  navigate({
                                    pathname: '/swap',
                                    search: createSearchParams({
                                      firstToken: pair.first_token_id,
                                      secondToken: pair.second_token_id
                                    }).toString()
                                  });
                                }}
                              >
                                <img src={SWAP_ICO} /> 
                                <div style={{ width: 60 }}>Swap</div>
                              </CustomButton>
                              <CustomButton
                                className={styles.button}
                                onClick={() => {
                                  if(userAddress) {
                                    setCurrentPair(pair);
                                    setShowAddLiquidityModal(true);
                                  }
                                }}
                              >
                                <img src={ADD_ICO} />
                                <div style={{ width: 60 }}>Add</div>
                              </CustomButton>
                              <CustomButton
                                className={styles.button}
                                onClick={() => {
                                  if(userAddress) {
                                    setCurrentPair(pair);
                                    setShowRemoveLiquidityModal(true);
                                  }
                                }}
                              >
                                <img src={REMOVE_ICO} />
                                <div style={{ width: 60 }}>Remove</div>
                              </CustomButton>
                            </div>
                            {
                              (userAddress && userAddress == pair.owner.toString() && pair.state.name == 'ActiveButNoSwap') ? (
                                <>
                                  <span className='col-12 text-center mt-4 text-white'>
                                    Pool Management
                                  </span>
                                  <div className='col-12 text-center mt-2 p-0'>
                                    <CustomButton
                                      className={clsx(styles.button, styles.manageBtn)}
                                      onClick={() => {
                                        if(!userAddress) {
                                          return;
                                        }

                                        enableSwap(pair);
                                      }}
                                    >
                                      Enable Swap
                                    </CustomButton>
                                  </div>
                                </>
                              ) : (
                                <></>
                              )
                            }
                          </AccordionDetails>
                        </Accordion>
                      );
                    })
                }
              </div>
              {
                pairs.length === 0 && (
                  fetching ? (
                    <Spinner className='text-white mt-5' animation="border" />
                  ) : (
                    <h3 className='text-white mt-5'>No Available Pools</h3>
                  )
                )
              }
            </div>
        </div>
      </div>
      
      <CustomModal
        title='Add Liquidity'
        show={showAddLiquidityModal}
        onClose={() => setShowAddLiquidityModal(false)}
      >
        <div className='d-flex flex-column justify-content-center align-items-start'>
          <h6 className='text-white text-left mt-4'>First Token Amount</h6>
          <span className='text-info'>{currentPair?.first_token_id}</span>
          <span className='text-white'>Balance: {shortenEsdt(currentPair?.first_token_balance)}</span>
          <CustomInput
              className={clsx(styles.import, 'flex-grow-1 w-100')}
              placeholder='First Token Amount'
              value={firstTokenAmount}
              maxValue={currentPair?.first_token_balance}
              setAmount={(first_value: BigNumber) => {
                const second_value = first_value
                  .multipliedBy(convertWeiToEsdt(currentPair.second_token_reserve, currentPair.second_token_decimal.toNumber()))
                  .dividedBy(convertWeiToEsdt(currentPair.first_token_reserve, currentPair.first_token_decimal.toNumber()));

                if(first_value.lte(currentPair.first_token_balance) && second_value.lte(egldPair ? egldBalance : currentPair.second_token_balance)) {
                    setFirstTokenAmount(first_value);
                    setSecondTokenAmount(second_value);
                }
              }}
          />
          <h6 className='text-white text-left mt-4'>Second Token Amount</h6>
          <span className='text-info'>{egldPair ? 'EGLD' : currentPair?.second_token_id}</span>
          <span className='text-white'>Balance: {shortenEsdt(egldPair ? egldBalance : currentPair?.second_token_balance)}</span>
          <CustomInput
              className={clsx(styles.import, 'flex-grow-1 w-100')}
              placeholder='Second Token Amount'
              value={secondTokenAmount}
              maxValue={egldPair ? egldBalance : currentPair?.second_token_balance}
              setAmount={(second_value: BigNumber) => {
                const first_value = second_value
                  .multipliedBy(convertWeiToEsdt(currentPair.first_token_reserve, currentPair.first_token_decimal.toNumber()))
                  .dividedBy(convertWeiToEsdt(currentPair.second_token_reserve, currentPair.second_token_decimal.toNumber()));
                
                if(first_value.lte(currentPair.first_token_balance) && second_value.lte(egldPair ? egldBalance : currentPair.second_token_balance)) {
                  setFirstTokenAmount(first_value);
                  setSecondTokenAmount(second_value);
                }
              }}
          />
          <span className='text-info mt-3'>1 {getSymbol(currentPair?.first_token_id || '')} ≃ {shortenEsdt(price, currentPair?.second_token_decimal.toNumber())} {getSymbol(currentPair?.second_token_id || '')}</span>
          <span className='text-info'>1 {getSymbol(currentPair?.second_token_id || '')} ≃ {shortenEsdt(new BigNumber(1).dividedBy(price), currentPair?.first_token_decimal.toNumber())} {getSymbol(currentPair?.first_token_id || '')}</span>

          {/* <div className={'text-white card shadow-sm mt-4'}>
            <div className={'d-flex justify-content-center w-100 px-2 mt-2'}>
                <span>You will be asked to sign { egldPair ? 2 : 1 } transactions</span>
            </div>
            {
                egldPair ? (
                    <>
                        <div className={'d-flex justify-content-between w-100 px-2 mt-2'}>
                            <span>1.</span>
                            <span>Wrap EGLD</span>
                        </div>
                        <div className={'d-flex justify-content-between w-100 px-2 mt-2'}>
                            <span>2.</span>
                            <span>Add Liquidity</span>
                        </div>
                    </>
                ) : (
                    <div className={'d-flex justify-content-between w-100 px-2 mt-2'}>
                        <span>1st</span>
                        <span>Add Liquidity</span>
                    </div>
                )
            }
          </div> */}
          <CustomButton
              className={styles.applyButton}
              onClick={() => addLp()}
          >
              Add
          </CustomButton>
        </div>
      </CustomModal>

      <CustomModal
        title='Remove Liquidity'
        show={showRemoveLiquidityModal}
        onClose={() => setShowRemoveLiquidityModal(false)}
      >
        <div className='d-flex flex-column justify-content-center align-items-start'>
          <h6 className='text-white text-left mt-4'>LP Token Amount</h6>
          <span className='text-info'>{currentPair?.lp_token_id}</span>
          <span className='text-white'>Balance: {shortenEsdt(currentPair?.lp_token_balance)}</span>
          {/* <input
              className={clsx(styles.import, 'flex-grow-1 w-100')}
              type='number'
              placeholder='Token Name'
              value={lpTokenAmount.toString()}
              onChange={(e) => {
                  const value = new BigNumber(+e.target.value);

                  if(value.lte(currentPair.lp_token_balance)) {
                    if(egldPair) {
                      const expectedAmount = currentPair.second_token_reserve
                        .multipliedBy(value)
                        .dividedBy(convertWeiToEsdt(currentPair.lp_token_supply, 18));
                      setExpectedWegldAmount(convertWeiToEsdt(expectedAmount, 18));
                    }
                    setLpTokenAmount(value);
                  }
              }}
          /> */}
          <CustomInput
              className={clsx(styles.import, 'flex-grow-1 w-100')}
              placeholder='LP Token Amount'
              value={lpTokenAmount}
              maxValue={currentPair?.lp_token_balance}
              setAmount={(value: BigNumber) => {
                if(value.lte(currentPair.lp_token_balance)) {
                  if(egldPair) {
                    const expectedAmount = currentPair.second_token_reserve
                      .multipliedBy(value)
                      .dividedBy(convertWeiToEsdt(currentPair.lp_token_supply, 18));
                    setExpectedWegldAmount(convertWeiToEsdt(expectedAmount, 18));
                  }
                  setLpTokenAmount(value);
                }
              }}
          />
          <CustomButton
              className={styles.applyButton}
              onClick={() => removeLp()}
          >
              Remove
          </CustomButton>
        </div>
      </CustomModal>
    </>
  );
};
